// assets/custom-cards-product-form.js
// Product form of the custom-product-card-rendering section (templates product.cards and
// product.card-rendering). Fork of custom-render-product-form.js, which the envelope section keeps;
// changes here cannot reach other templates. Defines the product-form element for this page.
// *** Custom: Add-ons hooks. When a <custom-addons> element (snippets/custom-addons.liquid) belongs to
// this form, the add-on fields are validated before anything is uploaded, and after the main card is
// in the cart every paid add-on is added as a nested line before the existing redirect runs. Pages
// without the element keep the original behaviour untouched.
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        // this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.form.addEventListener('submit', async (evt) => {
          evt.preventDefault();
          // *** Custom: Add-ons — stop here when a selected add-on is incomplete
          const addons = this.getAddons();
          if (addons && !addons.validate()) return;
          try {
            await canvasHandler.saveCanvasImage();
          } catch (error) {
            console.error('Error saving canvas image:', error);
          }
          this.onSubmitHandler.bind(this)(evt);
        });

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // *** Custom: the add-ons block bound to this form, if the page has one.
      // getAttribute on purpose: the form contains <input name="id">, and form controls shadow
      // HTMLFormElement properties, so this.form.id would return that input instead of the id.
      getAddons() {
        const formId = this.form ? this.form.getAttribute('id') : null;
        if (!formId) return null;
        const addons = document.querySelector(`custom-addons[data-form-id="${formId}"]`);
        return addons && typeof addons.addChildLines === 'function' ? addons : null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // *** Custom: Add-ons — dynamic checkout stays hidden while the submission runs
        const addons = this.getAddons();
        if (addons) addons.setSubmitting(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // *** Custom: Add-ons — the card is in the cart; add its paid add-on lines before finishing
            if (addons) {
              const result = await addons.addChildLines(response, {
                sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null,
                sectionsUrl: window.location.pathname,
              });
              if (!result.ok) {
                // The card must not be added twice: the button stays disabled, the add-ons panel
                // offers a retry and finishes the submission through custom:addons:completed
                this.error = true;
                this.handleErrorMessage(result.message);
                addons.addEventListener(
                  'custom:addons:completed',
                  (event) => {
                    this.error = false;
                    const completed = event.detail.response && event.detail.response.sections
                      ? { ...response, sections: event.detail.response.sections }
                      : response;
                    this.completeAdd(completed, formData);
                  },
                  { once: true }
                );
                return;
              }
              if (result.response && result.response.sections) response.sections = result.response.sections;
            }

            this.completeAdd(response, formData);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            // *** Custom: Add-ons
            if (addons) addons.setSubmitting(false);
          });
      }

      // Post-add behaviour: runs once the card and all its add-ons are in the cart.
      // *** Custom: the Buy buttons block's "Redirect after adding to cart" setting arrives as
      // data-redirect-url (snippets/custom-buy-buttons.liquid). Empty: stay here and open the cart notification.
      completeAdd(response, formData) {
        const redirectUrl = this.dataset.redirectUrl;
        if (redirectUrl) {
          window.location = redirectUrl;
          return;
        }
        if (!this.cart) {
          // window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.cart.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          this.cart.renderContents(response);
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
